import './styles/app.css';
import * as THREE from 'three';
import Stats from 'three/examples/jsm/libs/stats.module.js';
import { createSplatScene } from './splat/scene';
import { loadSplat } from './splat/loader';
import { installRevealEffect } from './splat/reveal';
import { createFocusMarker } from './splat/focus-marker';
import { createCameraController } from './camera/controller';
import { loadTour } from './tour/load';
import { createStore } from './state/store';
import { transition } from './state/machine';
import { installHistoryNav } from './state/history-nav';
import { attachLoadingBar } from './chrome/loading-bar';
import { clampOrbit, poseToSpherical } from './camera/orbit';
import type { Event, State } from './state/types';
import type { Pose } from './camera/orbit';

// Profiling toggle: ?profile in URL enables Stats overlay + renderer.info dump.
// ?debug enables the same Stats overlay plus verbose boot/GL logging to the
// console. (There is no on-screen DevTools overlay — Eruda was removed.)
const PROFILE = new URLSearchParams(location.search).has('profile');
const DEBUG = new URLSearchParams(location.search).has('debug');

// Perf-diagnostic toggles (also enable Stats overlay automatically). Use these
// to isolate which subsystem is costing frames on a particular machine:
//   ?noreveal     — skip the reveal shader patch (rules out our GLSL injection)
//   ?nooffaxis    — disable off-axis projection (rules out the wider frustum)
//   ?nosh         — render with sphericalHarmonicsDegree=0 (rules out SH cost)
// Multiple flags combine: e.g. /?noreveal&nooffaxis.
// (DPR is permanently capped at 1.0 in scene.ts — the previous ?lowdpr toggle
// was the bisect winner on Mac Studio and was promoted to default.)
const PERF = {
  noReveal: new URLSearchParams(location.search).has('noreveal'),
  noOffAxis: new URLSearchParams(location.search).has('nooffaxis'),
  noSh: new URLSearchParams(location.search).has('nosh')
};

const FLIGHT_BUMP = 0.18; // ~18% radius bulge at midpoint — subtle "lean back, then in"

async function boot() {
  const canvasEl = document.getElementById('splat-canvas');
  if (!(canvasEl instanceof HTMLCanvasElement)) throw new Error('splat-canvas not found');
  const canvas: HTMLCanvasElement = canvasEl;

  const loadingBar = attachLoadingBar();

  // Kick off font loading in parallel with splat fetch. We don't await it
  // here — the goal is just to start the network request now (the @font-face
  // rules are otherwise lazy: fonts only fetch when first text using them
  // renders, which would cause a FOUT on panel/stepper mount). UI mount
  // gates on this Promise, so by the time the splat is ready (typically
  // multi-second), fonts are loaded too.
  const fontsReady = (async () => {
    if (!('fonts' in document)) return;
    // Race against a 3s ceiling. If Google Fonts CSS hasn't loaded yet, or
    // the network is degraded, document.fonts.load() can hang — and a hung
    // promise here would block UI mount forever (mountUiIfReady gates on
    // fontsLoaded). 3s is well past typical font load time, but short enough
    // that a degraded user still sees the UI mount with fallback fonts.
    const TIMEOUT_MS = 3000;
    try {
      await Promise.race([
        Promise.all([
          document.fonts.load('1em Pattaya'),
          document.fonts.load('400 1em Merriweather'),
          document.fonts.load('700 1em Merriweather')
        ]),
        new Promise((resolve) => setTimeout(resolve, TIMEOUT_MS))
      ]);
    } catch (err) {
      console.warn('[boot] font load failed, proceeding with fallbacks', err);
    }
  })();

  const tour = await loadTour();
  if (PROFILE) console.log('[boot] tour loaded:', tour.hotspots.length, 'hotspots');

  const { scene, camera, renderer } = createSplatScene(canvas);

  // Profiler: Stats overlay (FPS / MS / MB) + periodic renderer.info dump.
  // Three panels are: 0=fps, 1=ms-per-frame, 2=memory (only Chrome with
  // performance.memory). Click panel to cycle.
  let stats: Stats | null = null;
  let lastInfoDump = 0;
  const anyPerfFlag = PERF.noReveal || PERF.noOffAxis || PERF.noSh;
  if (PROFILE || anyPerfFlag || DEBUG) {
    stats = new Stats();
    stats.dom.style.cssText = 'position:fixed;top:0;left:0;z-index:9999;';
    document.body.appendChild(stats.dom);
    const flags = Object.entries(PERF).filter(([, v]) => v).map(([k]) => k).join(',') || 'none';
    console.log(`[profile] FPS overlay enabled. perf flags: ${flags}. Click overlay to cycle panels.`);
  }

  // Dump renderer / GL capabilities up front when debugging. This is the
  // single most useful artifact when troubleshooting "splat doesn't render
  // on platform X" — the unmasked vendor/renderer string reveals whether
  // ANGLE is software-rendering, whether we got WebGL2, etc. Logging happens
  // BEFORE the splat fetch so it appears even if the load hangs.
  if (DEBUG || PROFILE) {
    const gl = renderer.getContext();
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    console.log('[gl] WebGL2:', gl instanceof WebGL2RenderingContext);
    console.log('[gl] vendor:', dbg ? gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) : '(masked)');
    console.log('[gl] renderer:', dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : '(masked)');
    console.log('[gl] version:', gl.getParameter(gl.VERSION));
    console.log('[gl] GLSL:', gl.getParameter(gl.SHADING_LANGUAGE_VERSION));
    console.log('[gl] max tex size:', gl.getParameter(gl.MAX_TEXTURE_SIZE));
    console.log('[gl] max tex units:', gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS));
  }

  const { mesh, ready, raycast: splatRaycast } = loadSplat(scene, '/' + tour.scene.asset, {
    onProgress: (loaded, total) => {
      // Pass through Spark's progress signal whenever it gives us a usable
      // ratio. The loading bar itself layers a time-based simulation on top,
      // so we don't have to fake when total is missing — just feed real
      // numbers when we have them.
      if (total > 0) loadingBar.setProgress(loaded / total);
    }
  });
  await ready;
  if (PROFILE || DEBUG) console.log('[boot] splat ready');

  // Post-load diagnostic: confirm the splat mesh is alive, in the scene, and
  // visible. If splats don't appear on the user's device, one of these will
  // explain it (mesh missing → load failed; not visible → was set hidden;
  // not parented → never added; getError != 0 → GPU-side failure during
  // texture upload that mkkellogg swallowed).
  if (DEBUG || PROFILE) {
    const sm = mesh.splatMesh as unknown as {
      visible?: boolean;
      parent?: unknown;
      geometry?: {
        attributes?: Record<string, { count?: number }>;
        instanceCount?: number;
        _maxInstanceCount?: number;
      };
      getSplatCount?: () => number;
      getMaxSplatCount?: () => number;
    } | null;
    console.log('[splat] mesh exists:', !!sm);
    if (sm) {
      console.log('[splat] visible:', sm.visible);
      console.log('[splat] parented:', !!sm.parent);
      const posAttr = sm.geometry?.attributes?.['position'];
      console.log('[splat] geometry position.count:', posAttr?.count ?? '(none)');
      // These four lines distinguish "lib knows about N splats" from
      // "GPU only draws N instances per frame". If getSplatCount is huge
      // but geometry.instanceCount is tiny, the sort is failing. If
      // getSplatCount itself is tiny, the load produced an undersized
      // buffer (parser bug or upstream-format incompatibility).
      console.log('[splat] getSplatCount:', sm.getSplatCount?.() ?? '(no method)');
      console.log('[splat] getMaxSplatCount:', sm.getMaxSplatCount?.() ?? '(no method)');
      console.log('[splat] geometry.instanceCount:', sm.geometry?.instanceCount ?? '(unset)');
      console.log('[splat] geometry._maxInstanceCount:', sm.geometry?._maxInstanceCount ?? '(unset)');
    }
    const gl = renderer.getContext();
    const err = gl.getError();
    console.log('[gl] getError after ready:',
      err === gl.NO_ERROR ? 'NO_ERROR' : `0x${err.toString(16)}`);
  }
  await loadingBar.dismiss();
  // Debug hooks for DevTools probing — only exposed when ?profile is set.
  if (PROFILE) Object.assign(window, { __splat: mesh, __scene: scene, __camera: camera, __renderer: renderer });

  // Radial reveal: a sphere expands from origin outward. Splats inside the
  // sphere render normally; splats outside fade out via a soft wavefront.
  // mkkellogg has no modifier system equivalent to Spark's dyno, so we patch
  // the splat ShaderMaterial's GLSL directly (see splat/reveal.ts). Radius
  // ramps linearly so the wavefront moves at constant speed.
  const splatMesh = mesh.splatMesh;
  const splatMaterial = splatMesh
    ? (splatMesh as THREE.Mesh).material as THREE.ShaderMaterial
    : null;
  // ?noreveal diagnostic flag: skip the shader patch entirely. Lets us
  // measure how much the per-vertex/fragment reveal compute costs on a
  // particular machine even after the animation finishes (the patch adds
  // a no-op compute every frame because we never strip it).
  const reveal = splatMaterial && !PERF.noReveal
    ? installRevealEffect(splatMaterial, 60)
    : null;
  reveal?.setCenter(0, 0, 0);
  const REVEAL_START = -60;
  const REVEAL_END = 600;
  const REVEAL_MS = 2800;
  reveal?.setRadius(REVEAL_START);
  if (PROFILE) Object.assign(window, { __reveal: reveal });

  const revealStart = performance.now();
  let revealDone = reveal === null; // if material wasn't available, skip the animation
  function tickReveal(now: number) {
    if (revealDone || !reveal) return;
    const t = Math.min(1, (now - revealStart) / REVEAL_MS);
    reveal.setRadius(REVEAL_START + (REVEAL_END - REVEAL_START) * t);
    if (t >= 1) {
      revealDone = true;
      tryMountUi();
    }
  }

  const store = createStore<State>({ kind: 'loading' });
  const dispatch = (e: Event) => {
    const next = transition(store.get(), e, performance.now());
    store.set(next);
  };

  // Make the mobile/browser Back button close the lightbox / 360° overlay
  // instead of unloading and restarting the whole app.
  installHistoryNav(store, dispatch);

  const controller = createCameraController(camera);
  const focusMarker = createFocusMarker(scene, camera);
  controller.attachInput(canvas, scene, {
    splatRaycast: (ndcX, ndcY) =>
      splatRaycast(camera, ndcX, ndcY, window.innerWidth, window.innerHeight)
  });
  controller.onDoubleClick((e) => {
    // Show the marker at the hit point, then let the state machine handle the
    // actual orbit-center change. The marker is purely a visual confirmation
    // that the input was received; it fades out on its own ~1.2s later.
    focusMarker.showAt(new THREE.Vector3(...e.worldPoint), performance.now());
    dispatch({ type: 'DOUBLE_CLICK', worldPoint: e.worldPoint });
  });
  controller.onAnimationDone(() => dispatch({ type: 'ANIMATION_DONE' }));

  function poseFromHotspot(idx: number): Pose {
    const h = tour.hotspots[idx]!;
    return {
      position: [...h.camera.position] as [number, number, number],
      target: [...h.camera.target] as [number, number, number],
      fov: h.camera.fov
    };
  }

  const offCameraSub = store.subscribe((s: State) => {
    switch (s.kind) {
      case 'loading':
        controller.setMode({ kind: 'idle' });
        break;
      case 'animating': {
        const fromPose = controller.getCurrentPose();
        const toPose = poseFromHotspot(s.toIndex);
        const toHotspot = tour.hotspots[s.toIndex]!;
        const toBounds = toHotspot.orbit ?? {};
        // Spherical state for both endpoints, each relative to its OWN target.
        // We clamp the DESTINATION spherical to the destination hotspot's
        // bounds so the flight lands inside the limits — no post-arrival snap,
        // and the bounds are honored as authored.
        const fromSph = poseToSpherical(fromPose, fromPose.target);
        const rawToSph = poseToSpherical(toPose, toPose.target);
        const toSph = clampOrbit(rawToSph, toBounds);
        controller.setMode({
          kind: 'animating',
          fromTarget: fromPose.target,
          fromSpherical: fromSph,
          toTarget: toPose.target,
          toSpherical: toSph,
          startedAt: performance.now(),
          durationMs: tour.defaults.cameraFlyDurationMs,
          bumpFactor: FLIGHT_BUMP
        });
        break;
      }
      case 'at-hotspot': {
        const h = tour.hotspots[s.index]!;
        // Authored bounds (h.orbit) were calibrated against h.camera.target.
        // They don't transfer to a user-clicked drifted target — applying them
        // would snap the camera in to fit a radius cap defined for a different
        // pivot. While drifted, run unbounded; restore authored bounds on
        // return to the canonical hotspot target (next GOTO or RECENTER).
        const isDrifted = s.drifted && s.driftedTarget !== null;
        const target = isDrifted ? s.driftedTarget! : h.camera.target;
        const bounds = isDrifted
          ? {}
          : h.orbit ?? { minPolar: 0.2, maxPolar: Math.PI - 0.2, minDistance: 5, maxDistance: 300 };
        controller.setMode({
          kind: 'orbit',
          target,
          bounds
        });
        break;
      }
      case 'lightbox':
      case 'pano':
        controller.setMode({ kind: 'idle' });
        break;
    }
  });

  // Special-case the loading→at-hotspot 0 transition: subscribe handler runs
  // AFTER store.set, but on initial load we want the camera placed at hotspot 0
  // immediately, not via an animation from wherever the camera happened to be.
  // Snap it into position before dispatching SPLAT_READY.
  //
  // ORDERING INVARIANT: this direct camera mutation MUST precede the
  // SPLAT_READY dispatch. While the controller is in 'idle' mode (set during
  // 'loading' state), its tick() is a no-op so it won't fight us. After
  // dispatch flips state to 'at-hotspot', the subscriber calls
  // controller.setMode({kind:'orbit',...}) which reads the current camera
  // pose via syncSphericalFromCamera and clamps it. If you reorder these
  // two blocks, the camera will jump to the controller's default spherical
  // (origin-relative) for one frame before the next tick corrects it.
  const startPose = poseFromHotspot(0);
  camera.position.set(startPose.position[0], startPose.position[1], startPose.position[2]);
  camera.lookAt(startPose.target[0], startPose.target[1], startPose.target[2]);

  dispatch({ type: 'SPLAT_READY' });

  // UI (pins + stepper) mounts AFTER the reveal animation completes so the
  // loading effect is uninterrupted. We pre-load the modules at boot so the
  // mount is instant when the reveal finishes (no async dynamic-import gap).
  const { mountPins } = await import('./pins/overlay');
  const { mountStepper } = await import('./chrome/stepper');
  const { mountPanel } = await import('./panel/panel');
  const { mountPanoViewer } = await import('./pano/viewer');
  const { mountLightboxViewer } = await import('./lightbox/viewer');
  type PinsHandleType = ReturnType<typeof mountPins>;
  type PanoHandleType = ReturnType<typeof mountPanoViewer>;
  type LightboxHandleType = ReturnType<typeof mountLightboxViewer>;
  let panoHandle: PanoHandleType | null = null;
  let lightboxHandle: LightboxHandleType | null = null;
  // Mount/dispose modal viewers when entering/leaving their states.
  const offPanoSub = store.subscribe((s: State) => {
    if (s.kind === 'pano' && !panoHandle) {
      panoHandle = mountPanoViewer(document.body, { src: s.src, dispatch });
    } else if (s.kind !== 'pano' && panoHandle) {
      panoHandle.dispose();
      panoHandle = null;
    }
    if (s.kind === 'lightbox' && !lightboxHandle) {
      lightboxHandle = mountLightboxViewer(document.body, { src: s.src, dispatch });
    } else if (s.kind !== 'lightbox' && lightboxHandle) {
      lightboxHandle.dispose();
      lightboxHandle = null;
    }
  });
  void offCameraSub;
  void offPanoSub;
  let uiMounted = false;
  let fontsLoaded = false;
  let pinsHandle: PinsHandleType | null = null;
  // Mount when both preconditions are met. Triggered by either:
  //  - tickReveal() flipping revealDone to true at the end of the animation
  //  - the fontsReady Promise resolving
  // Whichever fires last calls tryMountUi(); the early-exit guards against
  // double-mount.
  function tryMountUi() {
    if (uiMounted || !revealDone || !fontsLoaded) return;
    uiMounted = true;
    pinsHandle = mountPins({ scene, canvas, store, dispatch, tour, camera });
    mountStepper(document.body, { store, dispatch, tour });
    mountPanel(document.body, { store, dispatch, tour });
  }
  void fontsReady.then(() => {
    fontsLoaded = true;
    tryMountUi();
  });

  // Continuous rendering. Render-on-demand was a power optimization that
  // backfired on laptop GPUs: dropping renders during idle let the GPU
  // downclock, then the first frame of motion paid 20-30ms while the clock
  // ramped back up — perceived as judder. mkkellogg renders the full scene
  // in ~3ms on modern hardware, so the cost of "always render" is negligible
  // and the GPU stays in its hot power state for snappy response. mkkellogg
  // also requires renderer.render() to be called every frame so its splat
  // sort (driven by onBeforeRender on the DropInViewer's callbackMesh) stays
  // current — render-on-demand here would also stall the sort.

  function tick() {
    stats?.begin();
    const now = performance.now();
    tickReveal(now);
    controller.tick(now);
    pinsHandle?.tick(now);
    focusMarker.tick(now);
    renderer.render(scene, camera);
    if ((PROFILE || DEBUG) && now - lastInfoDump > 2000) {
      lastInfoDump = now;
      const info = renderer.info;
      const camPos = camera.position;
      const dist = Math.hypot(camPos.x, camPos.y, camPos.z).toFixed(1);
      console.log(
        `[profile] calls=${info.render.calls} tris=${info.render.triangles} ` +
          `geos=${info.memory.geometries} texs=${info.memory.textures} ` +
          `camDist=${dist}`
      );
    }
    stats?.end();
    requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);
}

// Visible error overlay for iOS Safari + any device where the user can't
// pop open DevTools. Catches three sources: window 'error' events (script
// throws, image/wasm load failures), unhandled promise rejections, and the
// boot() promise's rejection. First error wins (early-return) so a cascade
// of secondary failures doesn't bury the original cause.
function showFatalError(label: string, err: unknown): void {
  const msg = err instanceof Error
    ? `${err.name}: ${err.message}${err.stack ? '\n\n' + err.stack : ''}`
    : String(err);
  // Also dump to console so anyone with Web Inspector attached sees it.
  console.error(`[${label}]`, err);
  if (document.getElementById('__err_overlay')) return;
  const div = document.createElement('div');
  div.id = '__err_overlay';
  div.style.cssText =
    'position:fixed;inset:0;z-index:99999;padding:24px 16px;' +
    'background:rgba(0,0,0,0.92);color:#ffb3b3;' +
    'font:11px/1.45 ui-monospace,"SF Mono",Menlo,monospace;' +
    'overflow:auto;white-space:pre-wrap;' +
    '-webkit-user-select:text;user-select:text;-webkit-touch-callout:default;';
  div.textContent =
    `[${label}]\n\n${msg}\n\n---\nUA: ${navigator.userAgent}\n` +
    `WebGL2: ${typeof WebGL2RenderingContext !== 'undefined'}`;
  document.body.appendChild(div);
}

window.addEventListener('error', (e) =>
  showFatalError('window.error', e.error ?? `${e.message} @ ${e.filename}:${e.lineno}`));
window.addEventListener('unhandledrejection', (e) =>
  showFatalError('unhandledrejection', e.reason));

boot().catch((err) => showFatalError('boot', err));
